/** PG1-F7 (B27): the first-visit tour's spotlight, kept inside the viewport.
 *
 *  antd's Tour (@rc-component/tour) draws its mask from the target's
 *  `getBoundingClientRect()` widened by a 6 px gap: on a long page a target at the
 *  very top, or one taller than the window, gives a negative `top` or a bottom past
 *  the window, and the mask's `<rect>`s get a negative height or width ("A negative
 *  value is not valid" in the console). The tour gets a stand-in for the element
 *  whose rectangle is clipped to the window less that gap; the element itself is
 *  scrolled into view first when it is out of sight (the tour's own scroll would
 *  read the clipped rectangle and never fire). Pure apart from that scroll. */

/** The gap @rc-component/tour adds around a target by default. */
export const TOUR_GAP = 6;

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** `rect` clipped to a `vw` × `vh` window less `gap` on every side; never negative. */
export function clampToViewport(rect: Box, vw: number, vh: number, gap = TOUR_GAP): DOMRect {
  const left = Math.min(Math.max(rect.left, gap), Math.max(vw - gap, gap));
  const top = Math.min(Math.max(rect.top, gap), Math.max(vh - gap, gap));
  const right = Math.max(Math.min(rect.left + rect.width, vw - gap), left);
  const bottom = Math.max(Math.min(rect.top + rect.height, vh - gap), top);
  const box = { left, top, width: right - left, height: bottom - top };
  return {
    ...box,
    x: left,
    y: top,
    right,
    bottom,
    toJSON: () => box,
  } as DOMRect;
}

function outOfSight(rect: Box, vw: number, vh: number): boolean {
  return (
    rect.top + rect.height <= 0 || rect.top >= vh || rect.left + rect.width <= 0 || rect.left >= vw
  );
}

/** One stand-in per element: the tour stores what its target function returns in
 *  state on every render, so a new object each time would re-render for ever. */
const STAND_INS = new WeakMap<HTMLElement, HTMLElement>();

/** What the tour is given for `el`: the element, measured inside the window. */
export function tourTarget(el: HTMLElement): HTMLElement {
  const vw = () => window.innerWidth || document.documentElement.clientWidth;
  const vh = () => window.innerHeight || document.documentElement.clientHeight;
  const real = el.getBoundingClientRect();
  if (outOfSight(real, vw(), vh()) && typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ block: "center", inline: "nearest" });
  }
  const known = STAND_INS.get(el);
  if (known) return known;
  const standIn = new Proxy(el, {
    get(target, prop) {
      if (prop === "getBoundingClientRect") {
        return () => clampToViewport(target.getBoundingClientRect(), vw(), vh());
      }
      const value = Reflect.get(target, prop, target) as unknown;
      return typeof value === "function"
        ? (value as (...a: unknown[]) => unknown).bind(target)
        : value;
    },
  });
  STAND_INS.set(el, standIn);
  return standIn;
}
